import { Session } from '@supabase/supabase-js'

interface ApiRequestOptions {
  method?: string
  headers?: Record<string, string>
  body?: any
  session?: Session | null
  signal?: AbortSignal
}

const getApiBaseUrl = (): string => {
  const configuredBaseUrl = import.meta.env.VITE_API_URL
  if (configuredBaseUrl) {
    if (typeof window !== 'undefined') {
      const isProdHost = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
      const configuredIsLocalhost = configuredBaseUrl.includes('localhost') || configuredBaseUrl.includes('127.0.0.1')
      if (isProdHost && configuredIsLocalhost) {
        return window.location.origin
      }
    }

    return configuredBaseUrl
  }

  if (typeof window !== 'undefined') {
    const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    if (isLocalHost) {
      return 'http://localhost:3001'
    }

    return window.location.origin
  }

  return 'http://localhost:3001'
}

/**
 * Make an authenticated API request to the backend
 */
export const makeAuthenticatedRequest = async (
  endpoint: string, 
  options: ApiRequestOptions = {}
): Promise<Response> => {
  const baseUrl = getApiBaseUrl()
  const url = endpoint.startsWith('/') ? `${baseUrl}/api${endpoint}` : `${baseUrl}/api/${endpoint}`
  
  // Prepare headers
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers
  }
  
  // Add authorization header if we have a session
  if (options.session?.access_token) {
    headers['Authorization'] = `Bearer ${options.session.access_token}`
  }

  // Optional localhost dev bypass. Only attached when:
  //   1. Vite dev mode (import.meta.env.DEV)
  //   2. Browser is on a loopback hostname
  //   3. VITE_DEV_BYPASS_SECRET is set to a real secret that matches the
  //      backend's DEV_BYPASS_SECRET in rbac.ts.
  // The previous code shipped the literal string 'true' from any localhost
  // build, which became dead weight in prod and a footgun if a future server
  // change re-enabled bypass on the basic auth middleware.
  try {
    if (import.meta.env.DEV && typeof window !== 'undefined') {
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      const devBypassSecret = (import.meta.env.VITE_DEV_BYPASS_SECRET as string | undefined) || ''
      if (isLocal && devBypassSecret) {
        headers['x-dev-bypass'] = devBypassSecret
      }
    }
  } catch {}


  // Prepare fetch options
  const fetchOptions: RequestInit = {
    method: options.method || 'GET',
    headers,
    signal: options.signal,
  }
  
  // Add body if provided
  if (options.body) {
    fetchOptions.body = typeof options.body === 'string' 
      ? options.body 
      : JSON.stringify(options.body)
  }
  
  return fetch(url, fetchOptions)
}

/**
 * Make an authenticated API request and parse JSON response
 */
export const makeAuthenticatedJsonRequest = async <T = any>(
  endpoint: string, 
  options: ApiRequestOptions = {}
): Promise<T> => {
  let response: Response

  try {
    response = await makeAuthenticatedRequest(endpoint, options)
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error('Unable to reach the API server. Check that the backend is running and accessible.')
    }

    throw error
  }
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }))
    throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`)
  }
  
  return response.json()
}

/**
 * Common API endpoints
 */
export const API_ENDPOINTS = {
  PENTEST_SCAN: '/pentest/scan',
  PENTEST_ANALYZE: '/chat/analyze-pentest',
  ATTACK_PAYLOAD: '/attack/generate-payload',
  CHAT_MESSAGE: '/chat/message',
  THREAT_ANALYZE: '/threat/analyze',
  THREAT_SECURITY_SCAN: '/threat/security-scan',
} as const 
